import { Check, Plus } from 'lucide-react';
import { useState } from 'react';

import { Combobox } from '@apps/web/components/ui/combobox';
import {
    CommandEmpty,
    CommandGroup,
    CommandItem,
} from '@apps/web/components/ui/command';
import { cn } from '@apps/web/lib/utils';

type AnnotationSuggestion = {
    readonly label: string;
    readonly trace_count: number;
};

// Multi-select picker for a trace's annotations. Each catalog label shows a
// checkmark when it's applied to this trace; selecting a label toggles it on or
// off. Typing filters the catalog and offers a "Create" item when nothing
// matches. The popover stays open across toggles so several labels can be
// added/removed in a row.
export function AnnotationCombobox({
    open,
    onOpenChange,
    suggestions,
    selectedLabels,
    isLoading,
    onToggleLabel,
}: {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly suggestions: ReadonlyArray<AnnotationSuggestion>;
    readonly selectedLabels: ReadonlySet<string>;
    readonly isLoading: boolean;
    readonly onToggleLabel: (label: string) => void;
}) {
    const [search, setSearch] = useState('');
    const trimmed = search.trim();

    // Union the catalog with any applied label missing from it (e.g. a label
    // just created but not yet reflected in the cached catalog) so applied
    // labels always render as checked options.
    const countByLabel = new Map(
        suggestions.map((suggestion) => [
            suggestion.label,
            suggestion.trace_count,
        ]),
    );
    const labels = [
        ...suggestions.map((suggestion) => suggestion.label),
        ...[...selectedLabels].filter((label) => !countByLabel.has(label)),
    ];
    const hasExactMatch = labels.some(
        (label) => label.toLowerCase() === trimmed.toLowerCase(),
    );

    return (
        <Combobox
            open={open}
            onOpenChange={onOpenChange}
            placeholder="Add annotation…"
            searchValue={search}
            onSearchValueChange={setSearch}
            searchPlaceholder="Search or create annotation…"
            clearSearchOnClose={true}
        >
            <CommandEmpty>
                {isLoading ? 'Loading…' : 'Type to create a new annotation.'}
            </CommandEmpty>
            {trimmed.length > 0 && !hasExactMatch ? (
                <CommandGroup>
                    <CommandItem
                        value={trimmed}
                        onSelect={() => {
                            // Close over the typed value: cmdk lowercases the
                            // arg it passes to onSelect.
                            onToggleLabel(trimmed);
                            setSearch('');
                        }}
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Create “{trimmed}”
                    </CommandItem>
                </CommandGroup>
            ) : null}
            {labels.length > 0 ? (
                <CommandGroup heading="Annotations">
                    {labels.map((label) => (
                        <CommandItem
                            key={label}
                            value={label}
                            onSelect={() => onToggleLabel(label)}
                        >
                            <Check
                                className={cn(
                                    'mr-2 h-4 w-4',
                                    selectedLabels.has(label)
                                        ? 'opacity-100'
                                        : 'opacity-0',
                                )}
                            />
                            <span className="flex-1 truncate">{label}</span>
                            {countByLabel.has(label) ? (
                                <span className="text-muted-foreground text-xs">
                                    {countByLabel.get(label)}
                                </span>
                            ) : null}
                        </CommandItem>
                    ))}
                </CommandGroup>
            ) : null}
        </Combobox>
    );
}
